import React, {
  createContext,
  useContext,
  useRef,
  useState,
  useEffect,
  useCallback,
} from "react";
import { MAX_REQUEST_WAITING_TIME } from "shared/config/setting";
import { useAuth } from "shared/contexts/AuthProvider";
import { useEventBus } from "shared/services/eventBus";
import { CONNECTION_EVENTS, ROBOT_EVENTS, getRobotEvent } from "shared/services/eventBus/eventTypes";
import logger from "shared/utils/logging";
import {
  ConnectionStateMachine,
  CONNECTION_STATES
} from "shared/utils/stateMachine/ConnectionStateMachine";

// Error categories for better error handling
export const ERROR_TYPES = {
  NETWORK: 'NETWORK',
  AUTH: 'AUTH',
  PROTOCOL: 'PROTOCOL',
  TIMEOUT: 'TIMEOUT',
  UNKNOWN: 'UNKNOWN'
};

const ServerConnectionContext = createContext();

export const ServerConnectionProvider = ({ children, wsUrl }) => {
  // Get EventBus instance
  const eventBus = useEventBus();

  // State machine for connection lifecycle
  const stateMachineRef = useRef(null);
  if (!stateMachineRef.current) {
    stateMachineRef.current = new ConnectionStateMachine();
  }

  // State tracking (derived from state machine)
  const [connectionState, setConnectionState] = useState(CONNECTION_STATES.DISCONNECTED);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  const { auth } = useAuth();
  // Latest auth, read lazily by authenticate(): a token refresh must not close
  // and reopen the signaling socket (the server told every robot the user left
  // on each such reconnect, which killed their peer connections).
  const authRef = useRef(auth);
  authRef.current = auth;

  const wsRef = useRef(null);
  const clientIdRef = useRef(null);

  const reconnectTimeout = useRef(null);
  const [reconnectCountDown, setReconnectCountDown] = useState(null);
  const reconnectAttemptsRef = useRef(0);

  const waitingCmdResultCallbackRef = useRef(new Map());
  const waitingCmdResultCallback = waitingCmdResultCallbackRef.current;

  // Helper to categorize errors
  const categorizeError = (error) => {
    if (!error) return { type: ERROR_TYPES.UNKNOWN, message: 'Unknown error' };

    const errorMessage = error.message || error.toString();

    if (errorMessage.includes('timeout') || errorMessage.includes('TimeoutError')) {
      return { type: ERROR_TYPES.TIMEOUT, message: errorMessage, originalError: error };
    }
    if (errorMessage.includes('authenticate') || errorMessage.includes('auth')) {
      return { type: ERROR_TYPES.AUTH, message: errorMessage, originalError: error };
    }
    if (errorMessage.includes('network') || errorMessage.includes('connection')) {
      return { type: ERROR_TYPES.NETWORK, message: errorMessage, originalError: error };
    }
    if (errorMessage.includes('protocol') || errorMessage.includes('parse')) {
      return { type: ERROR_TYPES.PROTOCOL, message: errorMessage, originalError: error };
    }

    return { type: ERROR_TYPES.UNKNOWN, message: errorMessage, originalError: error };
  };

  // Helper to emit error events
  const emitError = (error, context = {}) => {
    const categorizedError = categorizeError(error);
    logger.error('ServerConnection error:', categorizedError);
    setError(categorizedError);
    eventBus.publish(CONNECTION_EVENTS.SERVER_ERROR, {
      ...categorizedError,
      ...context,
      timestamp: Date.now()
    });
  };

  const popWaitingCallback = (traceId) => {
    if (!traceId) return null;

    const entry = waitingCmdResultCallback.get(traceId);
    if (!entry) return null;

    const [callback, timeoutHandler] = entry;
    if (timeoutHandler) clearTimeout(timeoutHandler);
    waitingCmdResultCallback.delete(traceId);
    return callback;
  };

  const processCommandMessageTimeout = (trace_id) => {
    const return_callback = popWaitingCallback(trace_id);
    if (!return_callback) {
      return;
    }

    const timeoutError = new Error("TimeoutError: Request timed out");
    return_callback({ error: timeoutError });
  };

  const putWaitingCallback = (traceId, callback, timeout = 0) => {
    let timeoutHandler = null;

    if (timeout >= 0) {
      timeoutHandler = setTimeout(() => {
        processCommandMessageTimeout(traceId);
      }, timeout);
    }

    waitingCmdResultCallback.set(traceId, [callback, timeoutHandler]);
  };

  const sendRequestMessage = useCallback(async (
    message,
    timeout = MAX_REQUEST_WAITING_TIME
  ) => {
    const traceId = generateMsgId();
    const msg = {
      msg_type: "command_msg",
      msg_id: traceId,
      msg_body: message,
      msg_version: "0.2",
    };

    let timeoutId;

    return new Promise(async (resolve, reject) => {
      const returnCallback = ({ result = null, error = null }) => {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        const commandResult = {};

        if (result !== null) {
          commandResult.result = result;
        }
        if (error !== null) {
          commandResult.error = error;
        }
        // logger.log(`command ${traceId} return:`, commandResult);

        if (commandResult.error) {
          reject(commandResult.error);
        } else {
          resolve(commandResult.result);
        }
      };

      try {
        putWaitingCallback(traceId, returnCallback, timeout);

        await sendMessage(JSON.stringify(msg));
        // logger.log("command sent: ", msg);

        timeoutId = setTimeout(() => {
          popWaitingCallback(traceId);
          reject(new Error("TimeoutError"));
        }, timeout);
      } catch (err) {
        popWaitingCallback(traceId);
        reject(err);
      }
    }).finally(() => {
      const callback = popWaitingCallback(traceId);
      if (callback) {
        // logger.log(
        //   "sendRequestMessage finally: callback is not null. traceId: ",
        //   traceId
        // );
      }
    });
  }, []); // all internal deps (generateMsgId, putWaitingCallback, popWaitingCallback, sendMessage) are stable

  const generateMsgId = () => {
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    return btoa(String.fromCharCode(...bytes))
      .replace(/[/+=]/g, "")
      .substring(0, 22);
  };

  const sendMessage = useCallback(async (msg, to = null) => {
    if (wsRef.current == null || wsRef.current.readyState !== WebSocket.OPEN) {
      throw new Error("Can't send message. Connection is not open");
    }
    wsRef.current.send(msg);
  }, []); // wsRef is a stable ref — no deps needed

  const authenticate = () => {
    const socket = wsRef.current;
    if (socket.readyState != WebSocket.OPEN) return;

    const stateMachine = stateMachineRef.current;
    // Transition to AUTHENTICATING state
    if (!stateMachine.can('authenticate')) {
      logger.warn('Cannot authenticate: invalid state transition from', stateMachine.getState(), "to AUTHENTICATING");
      return;
    }
    stateMachine.transition('authenticate');
    setConnectionState(CONNECTION_STATES.AUTHENTICATING);

    const msg = { cmd: "authenticate", data: { token: authRef.current?.token } };
    socket.send(JSON.stringify(msg));

  };

  // Put the state machine back to DISCONNECTED when we deliberately drop the
  // socket, so that the next connect()/authenticate() transitions are valid.
  const markDisconnected = () => {
    const stateMachine = stateMachineRef.current;
    if (stateMachine.can('disconnect')) {
      stateMachine.transition('disconnect');
      setConnectionState(CONNECTION_STATES.DISCONNECTED);
    }
  };

  const connect = () => {
    // logger.log("create a new WebSocket");

    // Transition to CONNECTING state
    const stateMachine = stateMachineRef.current;
    if (!stateMachine.can('connect')) {
      logger.warn('Cannot connect: invalid state transition from', stateMachine.getState(), "to CONNECTING");
      // return;
    }
    stateMachine.transition('connect');
    setConnectionState(CONNECTION_STATES.CONNECTING);

    const userWsUrl = clientIdRef.current
      ? `${wsUrl}?prev_client_id=${encodeURIComponent(clientIdRef.current)}`
      : wsUrl;

    const socket = new WebSocket(userWsUrl);
    // Events of a socket that has been replaced (effect re-run, explicit close)
    // must not drive the state machine or schedule reconnects.
    const isCurrent = () => wsRef.current === socket;

    socket.onopen = () => {
      // logger.log("ws connection open: " + wsUrl);
      if (!isCurrent()) {
        socket.close();
        return;
      }

      setError(null);
      authenticate();
    };

    socket.onerror = (err) => {
      if (!isCurrent()) return;
      logger.error("WebSocket connection error:", err);

      // Transition to FAILED state
      if (stateMachine.can('fail')) {
        stateMachine.transition('fail');
        setConnectionState(CONNECTION_STATES.FAILED);
      }

      emitError(new Error('WebSocket connection failed'), { url: wsUrl });
    };

    socket.onclose = (event) => {
      // logger.log("ws connection onclose: " + wsUrl);
      // logger.log("WebSocket close code:", event.code);
      if (!isCurrent()) {
        logger.debug("superseded WebSocket closed");
        return;
      }

      // Check if this was an expected closure
      const currentState = stateMachine.getState();

      // If we're already disconnected or in a terminal state, don't reconnect
      if (currentState === CONNECTION_STATES.DISCONNECTED) {
        return;
      }

      // Unexpected closure - attempt reconnect if socket ref still exists
      if (wsRef.current != null) {
        reconnectWithBackoff();
      } else {
        // Explicit disconnect - transition to DISCONNECTED
        if (stateMachine.can('disconnect')) {
          stateMachine.transition('disconnect');
          setConnectionState(CONNECTION_STATES.DISCONNECTED);
        }
      }

      setError(null);
    };

    socket.onmessage = (event) => {
      if (!isCurrent()) return;
      const message = event.data;
      // logger.debug("ws onmessage: ", message);
      if (isCommandReturnMessage(message)) {
        // logger.log("processCommandReturnMessage: ", message);
        processCommandReturnMessage(message);
        return;
      } else {
        setMessage(event.data);
        try {
          handleMessageReceived(event.data);
        } catch (e) {
          logger.error("Error handling message: ", e);
          emitError(e, { context: 'handleMessageReceived' });
        }
      }
    };

    wsRef.current = socket;
  };

  const isCommandReturnMessage = (data) => {
    try {
      let message = JSON.parse(data);
      const msgType = message?.msg_type;
      // logger.log("isCommandReturnMessage: msgType =", msgType);
      if (msgType === "command_return") {
        return true;
      }
    } catch (e) {
      logger.debug("isCommandReturnMessage: Error parsing message:", e);
    }
    return false;
  };

  const processCommandReturnMessage = async (data) => {
    let message = JSON.parse(data);
    const msgType = message?.msg_type;

    const traceId = message?.trace_id;

    if (msgType !== "command_return") {
      return false;
    }

    if (!traceId) {
      logger.warn("trace_id is null or undefined");
    }

    const returnCallback = popWaitingCallback(traceId);
    if (returnCallback === null || returnCallback === undefined) {
      return true;
    }

    const msgBody = message["msg_body"];

    try {
      const maybePromise = returnCallback(msgBody);
      if (maybePromise instanceof Promise) {
        await maybePromise;
      }
    } catch (err) {
      logger.error("Error executing returnCallback:", err);
    }

    return true;
  };

  // Exponential backoff reconnection
  const reconnectWithBackoff = () => {
    const stateMachine = stateMachineRef.current;

    // Transition to RECONNECTING state
    if (stateMachine.can('reconnect')) {
      stateMachine.transition('reconnect');
      setConnectionState(CONNECTION_STATES.RECONNECTING);
    }

    // Calculate exponential backoff delay
    const attempt = reconnectAttemptsRef.current;
    const baseDelay = 1000; // 1 second
    const maxDelay = 30000; // 30 seconds
    const delay = Math.min(baseDelay * Math.pow(2, attempt), maxDelay);

    reconnectAttemptsRef.current += 1;

    logger.debug(
      `Reconnecting in ${delay}ms (attempt ${attempt + 1})...`
    );

    if (reconnectTimeout.current) {
      clearTimeout(reconnectTimeout.current);
    }

    setReconnectCountDown(Math.ceil(delay / 1000));

    reconnectTimeout.current = setTimeout(() => {
      logger.debug('Attempting reconnection...');
      connect();
    }, delay);
  };

  // Legacy reconnect function (for backward compatibility)
  const reconnect = (countdown = 0) => {
    logger.debug(
      "Reconnect to websocket server after " + countdown + " seconds."
    );
    if (reconnectTimeout.current) {
      clearTimeout(reconnectTimeout.current);
    }
    setReconnectCountDown(countdown);
    if (countdown > 0) {
      reconnectTimeout.current = setTimeout(
        () => reconnect(countdown - 1),
        1000
      );
      return;
    }
    connect();
  };

  const closeConnection = useCallback(() => {
    // Transition to DISCONNECTED state
    const stateMachine = stateMachineRef.current;
    if (stateMachine.can('disconnect')) {
      stateMachine.transition('disconnect');
      setConnectionState(CONNECTION_STATES.DISCONNECTED);
    }

    const socket = wsRef.current;
    if (socket != null && socket.readyState == WebSocket.OPEN) {
      socket.close();
      wsRef.current = null;
    }

    if (reconnectTimeout.current) {
      clearTimeout(reconnectTimeout.current);
    }

    // Reset reconnect attempts
    reconnectAttemptsRef.current = 0;
  }, []); // only uses refs — stable


  const handleMessageReceived = (message) => {
    try {
      let answer = JSON.parse(message);

      let msg_type = null;
      if ("type" in answer) {
        msg_type = answer["type"];
      } else if ("msg_type" in answer) {
        msg_type = answer["msg_type"];
      }

      if (msg_type == "Info") {
        const clientId = answer["ClientId"];
        clientIdRef.current = clientId;
        logger.debug("user client id: ", clientId);
        
        // After successful authentication, transition to CONNECTED
        const stateMachine = stateMachineRef.current;
        if (!stateMachine.can('authenticated')) {
          logger.warn('Cannot connect: invalid state transition from', stateMachine.getState(), "to CONNECTED");
          // return;
        }
        stateMachine.transition('authenticated');
        setConnectionState(CONNECTION_STATES.CONNECTED);
        // Reset reconnect attempts on successful connection
        reconnectAttemptsRef.current = 0;        
      } else if (msg_type === "answer") {
        const from_rid = answer?.from;
        if (from_rid) {
          eventBus.publish(getRobotEvent(from_rid, ROBOT_EVENTS.SIGNAL_SDP), answer);
        } else {
          logger.warn("[ServerConnection] Received 'answer' message with no 'from' field");
        }
      } else if (msg_type === "p2p_event") {
        const data = answer?.data || answer?.msg_body;
        const robot_client_id = data["robot_client_id"];
        logger.debug("p2p_event: ", answer);
        eventBus.publish(getRobotEvent(robot_client_id, ROBOT_EVENTS.P2P_EVENT), data);
      } else if (msg_type === "robot_event") {
        const data = answer?.data || answer?.msg_body;
        if (data["event_type"] === "connected") {
          let event_data = data["event_data"];
          let rid = event_data["id"];
          eventBus.publish(getRobotEvent(rid, ROBOT_EVENTS.CONNECTED), event_data);
        } else if (data["event_type"] === "disconnected") {
          let event_data = data["event_data"];
          let rid = event_data["id"];
          eventBus.publish(getRobotEvent(rid, ROBOT_EVENTS.DISCONNECTED), event_data);
        } else if (data["event_type"] === "cnn_peers_updated") {
          const event_data = data["event_data"];
          const robot_client_id = event_data["robot_client_id"];
          const connecting_user_clients =
            event_data["connecting_user_clients"] || [];
          logger.debug("cnn_peers_updated: ", event_data);
          eventBus.publish(getRobotEvent(robot_client_id, ROBOT_EVENTS.PEERS_UPDATED), event_data);
        }
      } else if (msg_type === "signal") {
        const data = answer["data"];
        const rid = answer["from"];
        eventBus.publish(getRobotEvent(rid, ROBOT_EVENTS.SIGNAL_CANDIDATE), data);
      } else {
        logger.warn("unknown answer type: ", answer);
      }
    } catch (e) {
      logger.error("Error: " + e + "\nMessage data: " + message);
    }
  };

  // Sync state machine changes with React state and publish EventBus events
  useEffect(() => {
    const stateMachine = stateMachineRef.current;
    const unsubscribe = stateMachine.addListener((currentState, previousState, transition) => {
      // logger.debug(`Connection state: ${previousState} -> ${currentState} (${transition})`);

      // Update React state
      setConnectionState(currentState);

      // Publish EventBus events
      eventBus.publish(CONNECTION_EVENTS.SERVER_STATE_CHANGED, {
        state: currentState,
        prevState: previousState,
        transition
      });

      // Publish specific state events
      if (currentState === CONNECTION_STATES.CONNECTED) {
        eventBus.publish(CONNECTION_EVENTS.SERVER_OPENED, { url: wsUrl });
      } else if (currentState === CONNECTION_STATES.DISCONNECTED) {
        eventBus.publish(CONNECTION_EVENTS.SERVER_CLOSED, { url: wsUrl });
      }
    });

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [eventBus, wsUrl]);

  // Only the URL and the logged-in flag decide whether a socket exists; token
  // refreshes are picked up through authRef on the next authenticate().
  const isAuth = !!auth?.isAuth;
  useEffect(() => {
    if (!isAuth) {
      // logger.log("useWS useEffect: not authenticated");
      closeConnection();
      // a client id belongs to one login session: the next user must not
      // resume it through prev_client_id
      clientIdRef.current = null;
      return;
    }
    if (
      wsRef.current == null ||
      wsRef.current.readyState == WebSocket.CLOSING ||
      wsRef.current.readyState == WebSocket.CLOSED
    ) {
      connect();
    } else {
      logger.debug("useWS effect: use previous websocket");
      authenticate();
    }

    return () => {
      const socket = wsRef.current;
      logger.debug("useWS useEffect exit: readyState =", socket?.readyState);

      if (socket != null && socket.readyState != WebSocket.CLOSED) {
        wsRef.current = null; // detach first: its onclose must not reconnect
        socket.close();
        logger.debug("useWS useEffect exit: close socket");
        markDisconnected();
      }
      if (reconnectTimeout.current) {
        logger.debug("useWS useEffect exit: clear reconnection timer");
        clearTimeout(reconnectTimeout.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wsUrl, isAuth]);

  // useEffect(() => {
  //   authenticate();
  // }, [auth]);

  // Backward compatibility: derive isOpen and isConnecting from connectionState
  const isOpen = connectionState === CONNECTION_STATES.CONNECTED;
  const isConnecting = connectionState === CONNECTION_STATES.CONNECTING ||
                       connectionState === CONNECTION_STATES.AUTHENTICATING;

  const ret = {
    // New state-based API
    connectionState,
    CONNECTION_STATES,

    // Legacy API (for backward compatibility)
    isOpen,
    isConnecting,
    message,
    sendMessage,
    sendRequestMessage,
    error,
    reconnectCountDown,

    // New API methods
    closeConnection,
    reconnect: reconnectWithBackoff, // Use new backoff logic by default
  };

  return (
    <ServerConnectionContext.Provider value={ret}>
      {children}
    </ServerConnectionContext.Provider>
  );
};

export const useServerConnection = () => useContext(ServerConnectionContext);
